import { App } from "vue";
import storage from "store";
import router from "@/router";
import { regAxios } from "./install";
import { message } from "ant-design-vue";
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { clearStorage } from "@/utils/common";
export const isLocal = location.href.indexOf("localhost") > -1;
import { CHINESE_CODE } from "@/store/modules/language";
import { YzsRequestHelper } from "./yzsRequestHelper";
// 创建axios实例
const axiosInstance = axios.create({
  baseURL: "/",
  timeout: 6000,
  withCredentials: true,
});
const noErrMessagePath = [
  "/harbour-server/jwt/refreshSession",
  "/iam-auth-web/auth/user/renewal/token"
];
const renderCorePathPrefixes = [
  "/base-biz-data-server/",
  "/common-query-web/",
  "/iam-auth-web/",
];

function getRequestPath(url?: string) {
  if (!url) {
    return "";
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    return url;
  }
}

function normalizeRenderCoreRequestUrl(url?: string) {
  if (!url) {
    return url;
  }

  const requestPath = getRequestPath(url);
  const matchedPrefix = renderCorePathPrefixes.find((prefix) =>
    requestPath.includes(prefix)
  );
  if (!matchedPrefix) {
    return url;
  }

  const startIndex = requestPath.indexOf(matchedPrefix);
  return `/api${requestPath.slice(startIndex)}`;
}

function isRenderCoreDataResponse(url?: string) {
  const requestPath = getRequestPath(url);

  return (
    requestPath.includes("/base-biz-data-server/query/data/apiDataSource/") ||
    requestPath.includes("/common-query-web/query/common/biz/kv/")
  );
}

function getAcceptLanguageHeader(language: string) {
  let languages = [language, language.split("-")[0], ...navigator.languages];
  languages = languages.filter(
    (item: string, index: number) => languages.indexOf(item) === index
  );
  return languages
    .map((lang, index) => {
      return index === 0 ? lang : `${lang};q=${(10 - index) / 10}`;
    })
    .join(",");
}

/**
 * @desc: 异常拦截处理器
 * @param { Object } error 错误信息
 */
const errorHandler = (error: AxiosError): AxiosError | Promise<AxiosError> => {
  for (const path of noErrMessagePath) {
    if (error.config.url?.includes(path)) {
      //token刷新接口不弹窗
      return Promise.reject(error);
    }
  }
  if (error.response?.status === 401) {
    // 登录失效
    // storage.clearAll();
    // localStorage.clear();
    clearStorage();
    message.error("登录已失效，请重新登录");
    if (router.currentRoute.value.path !== "/login") {
      router.push({
        path: "/login",
        query: { redirect: router.currentRoute.value.fullPath },
      });
    }
    return Promise.reject(error);
  }
  // eslint-disable-next-line no-console
  console.error(error.response, "error.response");
  message.error(error.response?.data?.message || "请求异常");
  return Promise.reject(error);
};

/**
 * @desc: 请求发送前拦截
 * @param { Object } config 配置参数
 */
axiosInstance.interceptors.request.use(
  (config: AxiosRequestConfig): AxiosRequestConfig => {
    config.url = normalizeRenderCoreRequestUrl(config.url);

    const token = storage.get("token");
    const defaultHeaders = {
      Authorization: token ? String(token) : "",
      "X-Requested-By": "eshipping",
      "X-Request-From": "YZSALL.harbour-portal-pc",
      "Accept-Language": getAcceptLanguageHeader(
        storage.get("language") || CHINESE_CODE
      ),
    };

    // 合并默认headers和自定义headers
    const customHeaders = config.headers || {};
    const finalHeaders = {
      ...defaultHeaders,
      ...YzsRequestHelper.buildHeaders(config.params),
      ...customHeaders,
    };

    // 如果某个header值为null，则删除该header
    Object.keys(finalHeaders).forEach((key) => {
      if (finalHeaders[key] === null) {
        delete finalHeaders[key];
      }
    });

    return {
      ...config,
      headers: finalHeaders,
    };
  },
  errorHandler
);

/**
 * @desc: 服务端响应后拦截
 * @param { Object } response 返回的数据
 */
axiosInstance.interceptors.response.use((response: AxiosResponse) => {
  if (isRenderCoreDataResponse(response.config.url)) {
    return response.data;
  }

  for (const path of noErrMessagePath) {
    if (response.config.url?.includes(path)) {
      //token刷新接口不弹窗
      return response;
    }
  }
  if (response.data.success) {
    return response;
  } else {
    // 验证码登录不弹窗
    if (response.data?.errCode != "102") {
      message.error(response.data.message || response.data.errMessage);
    }
    return Promise.reject(response);
  }
}, errorHandler);

// 扩展请求方法，支持合并配置
const request = (
  config: AxiosRequestConfig,
  extraConfig?: AxiosRequestConfig
) => {
  const mergedConfig = {
    ...config,
    ...extraConfig,
    headers: {
      ...(config.headers || {}),
      ...(extraConfig?.headers || {}),
    },
  };
  return axiosInstance(mergedConfig);
};

// 导出 Vue 插件
export const globalAxios = {
  install(app: App) {
    regAxios.install(app, axiosInstance);
  },
};

export default request;
